import { NavLeaf, NavMatch, NavModule, NavSection } from '../models/navigation.model';
import { ROUTE } from '@shared/constants';
import { PageHeader } from '../models/page-header.model';

/** Modules only super admins may see — the flag lives here so the module definitions stay uniform data. */
const SUPER_ADMIN_MODULE_IDS = new Set(['admin', 'dev', 'support']);

const MODULES: NavModule[] = [
  {
    id: 'my',
    code: 'ME',
    name: 'My Work',
    icon: 'userRound',
    tint: 'sky',
    groups: [
      {
        // Label-less group → renders as a direct link under ME (no sub-heading).
        id: 'my-shift',
        label: '',
        icon: 'clock',
        items: [
          {
            id: 'my-shift-schedule',
            label: 'My Shift',
            icon: 'clock',
            route: ROUTE.HRM_ROSTER_ME.path,
            description: 'Your published shifts for the selected period.',
            permission: ROUTE.HRM_ROSTER_ME.permission,
          },
        ],
      },
      {
        id: 'my-time',
        label: '',
        icon: 'timer',
        items: [
          {
            id: 'my-timesheet',
            label: 'My Timesheet',
            icon: 'timer',
            route: ROUTE.HRM_ATTENDANCE_ME.path,
            // There is no submit step — see the MyTimesheetComponent docblock: a manager
            // reviews and approves worked days from the Approval Queue. Saying otherwise sent
            // people looking for a button that was never there.
            description:
              'Your rostered and worked days. Your manager approves them — nothing to submit.',
            permission: ROUTE.HRM_ATTENDANCE_ME.permission,
          },
        ],
      },
      {
        // Label-less group → renders as a direct link under ME (no sub-heading).
        id: 'my-tickets',
        label: '',
        icon: 'ticket',
        items: [
          {
            id: 'my-tickets-list',
            label: 'My Tickets',
            icon: 'ticket',
            route: ROUTE.HRM_ATTENDANCE_TICKETS_ME.path,
            description: 'Every ticket you have raised, and where each one stands.',
            permission: ROUTE.HRM_ATTENDANCE_TICKETS_ME.permission,
          },
        ],
      },
      {
        // Label-less group → renders as a direct link under ME (no sub-heading).
        id: 'my-leave',
        label: '',
        icon: 'plane',
        items: [
          {
            id: 'my-leave-requests',
            label: 'My Leave',
            icon: 'plane',
            route: ROUTE.HRM_LEAVE_ME.path,
            description: 'Your leave requests — apply, edit, or withdraw.',
            permission: ROUTE.HRM_LEAVE_ME.permission,
          },
        ],
      },
      {
        // Label-less group → renders as a direct link under ME (no sub-heading). The unified Tasks
        // screen replaces the separate "My Work" (runs) and "My Sign-offs" (acknowledgements) inboxes.
        id: 'my-tasks-nav',
        label: '',
        icon: 'listChecks',
        items: [
          {
            id: 'pms-tasks',
            label: 'My Tasks',
            icon: 'listChecks',
            route: ROUTE.TASKS.path,
            description: 'Your runs and sign-off requests to complete, plus every assigned task.',
            permission: ROUTE.TASKS.permission,
          },
        ],
      },
      {
        // Label-less group → renders as a direct link under ME (no sub-heading). Being a
        // contractor only makes the leaf eligible; it still needs the grant, matching the route guard
        // and MyReportController — otherwise a contractor with no permissions sees a link that bounces
        // them to the dashboard.
        id: 'my-billing',
        label: '',
        icon: 'badgeDollarSign',
        items: [
          {
            id: 'bms-my-billing-report',
            label: 'My Billing',
            icon: 'badgeDollarSign',
            route: ROUTE.BMS_REPORTS_ME.path,
            description:
              'Your billing periods — what was collected, the service fee, and the amount remitted.',
            permission: ROUTE.BMS_REPORTS_ME.permission,
            externalContractorOnly: true,
          },
        ],
      },
      {
        id: 'my-documents',
        label: 'Employee Documents',
        icon: 'folder',
        items: [
          {
            id: 'hrm-documents-to-sign',
            label: 'Documents to Sign',
            icon: 'signature',
            route: ROUTE.HRM_MY_SIGNATURES.path,
            description: 'HRM documents awaiting your signature.',
            permission: ROUTE.HRM_MY_SIGNATURES.permission,
          },
          {
            id: 'hrm-my-documents',
            label: 'My Documents',
            icon: 'folderOpen',
            route: ROUTE.HRM_MY_DOCUMENTS.path,
            description: 'Your documents and signed files.',
            permission: ROUTE.HRM_MY_DOCUMENTS.permission,
          },
        ],
      },
      {
        id: 'my-profile',
        label: 'Account',
        icon: 'user',
        items: [
          {
            id: 'my-profile-page',
            label: 'Profile',
            icon: 'circleUser',
            route: ROUTE.PROFILE.path,
            description: 'Your account, roles and effective permissions.',
          },
        ],
      },
    ],
  },
  {
    id: 'hrm',
    code: 'HR',
    name: 'People & Culture',
    icon: 'users',
    tint: 'emerald',
    groups: [
      {
        id: 'hrm-leave',
        label: 'Leave & Roster',
        icon: 'calendar',
        items: [
          {
            id: 'hrm-shift-roster',
            label: 'Shift Roster',
            icon: 'calendarRange',
            route: ROUTE.HRM_ROSTER.path,
            description: 'Rosters and shift planning.',
            permission: ROUTE.HRM_ROSTER.permission,
          },
          {
            id: 'hrm-leave-management',
            label: 'Leave Management',
            icon: 'calendarX',
            route: ROUTE.HRM_LEAVE.path,
            description: 'Leave requests and approvals.',
            permission: ROUTE.HRM_LEAVE.permission,
          },
        ],
      },
      {
        id: 'hrm-attendance-group',
        label: 'Time & Attendance',
        icon: 'clock',
        items: [
          {
            id: 'hrm-attendance',
            label: 'Attendance',
            icon: 'fingerprint',
            route: ROUTE.HRM_ATTENDANCE.path,
            description: 'Track and manage staff attendance',
            permission: ROUTE.HRM_ATTENDANCE.permission,
          },
          {
            id: 'hrm-attendance-analytics',
            label: 'Attendance Reports',
            icon: 'chartColumn',
            route: ROUTE.HRM_ATTENDANCE_ANALYTICS.path,
            // No description: the page states its own period, head count and baseline in a line
            // above the figures, and a subtitle listing the widgets underneath it added nothing.
            permission: ROUTE.HRM_ATTENDANCE_ANALYTICS.permission,
          },
        ],
      },
      {
        // Tickets are NOT attendance-only: the type list runs from timesheet regularisation and leave
        // through to expenses, IT support and complaints, so the entry sits under its own heading rather
        // than inside Time & Attendance.
        id: 'hrm-tickets',
        label: 'Support',
        icon: 'ticket',
        items: [
          {
            id: 'hrm-attendance-tickets',
            label: 'Tickets',
            icon: 'ticket',
            route: ROUTE.HRM_ATTENDANCE_TICKETS.path,
            description: 'Every ticket raised by staff — review, approve or reject.',
            permission: ROUTE.HRM_ATTENDANCE_TICKETS.permission,
          },
        ],
      },
      {
        id: 'hrm-documents',
        label: 'HR Documents',
        icon: 'folder',
        items: [
          {
            id: 'hrm-template',
            label: 'Document Templates',
            icon: 'fileText',
            route: ROUTE.HRM_TEMPLATES.path,
            description: 'Manage document templates, folders and signature requests.',
            permission: ROUTE.HRM_TEMPLATES.permission,
          },
        ],
      },
    ],
  },
  {
    id: 'pms',
    code: 'SOP',
    name: 'Operation',
    icon: 'clipboardCheck',
    tint: 'azure',
    groups: [
      {
        // The sidebar renders each group heading as a small, non-collapsible label, so SOP still lists
        // its screens directly (Platform changes 20.8.26) — the headings only divide the list.
        id: 'pms-gov',
        label: 'Governance',
        icon: 'shield',
        items: [
          {
            id: 'pms-policies',
            label: 'Policies',
            icon: 'scrollText',
            route: ROUTE.PMS_POLICIES.path,
            description: 'Author and publish governance policies.',
            permission: ROUTE.PMS_POLICIES.permission,
          },
          {
            id: 'pms-procedures',
            label: 'Procedures',
            icon: 'listOrdered',
            route: ROUTE.PMS_PROCEDURES.path,
            permission: ROUTE.PMS_PROCEDURES.permission,
          },
          {
            id: 'pms-processes',
            label: 'Processes',
            icon: 'workflow',
            description: 'Model and track end-to-end business processes.',
            route: ROUTE.PMS_PROCESSES.path,
            permission: ROUTE.PMS_PROCESSES.permission,
          },
        ],
      },
      {
        id: 'pms-execution',
        label: 'Execution',
        icon: 'checkSquare',
        items: [
          {
            // The client's SOP list is Policies / Procedures / Processes / Tasks. This is the MANAGER
            // board of everyone's tasks — a screen of its own, separate from ME > My Tasks, which is
            // the caller's own inbox.
            id: 'pms-sop-tasks',
            label: 'Assigned Tasks',
            icon: 'squareCheckBig',
            route: ROUTE.PMS_ASSIGNED_TASKS.path,
            description: 'Every assigned task across the practice, by week and by folder.',
            permission: ROUTE.PMS_ASSIGNED_TASKS.permission,
          },
        ],
      },
      {
        id: 'pms-work',
        label: 'Workspace',
        icon: 'folder',
        items: [
          {
            id: 'pms-files',
            label: 'Document Library',
            icon: 'library',
            route: ROUTE.PMS_FILES.path,
            description: 'Browse every document and folder in one tree.',
            permission: ROUTE.PMS_FILES.permission,
          },
          {
            id: 'pms-evidence',
            label: 'Evidence',
            icon: 'fileCheck',
            route: ROUTE.PMS_EVIDENCE.path,
            description: `Every procedure's proof, in one explorer.Open a process, procedure or step
              like a folder to reach its evidence — or search by name across all of them.`,
            permission: ROUTE.PMS_EVIDENCE.permission,
          },
          {
            id: 'pms-tags',
            label: 'Tags',
            icon: 'tag',
            route: ROUTE.PMS_TAGS.path,
            description: 'Tags used to categorise documents.',
            permission: ROUTE.PMS_TAGS.permission,
          },
        ],
      },
    ],
  },
  {
    id: 'bms',
    code: 'BMS',
    name: 'Billings',
    icon: 'receipt',
    tint: 'lagoon',
    groups: [
      {
        // Label-less group → the items render directly under BMS (no "Billing" sub-heading).
        id: 'bms-billing',
        label: '',
        icon: 'dollar',
        items: [
          {
            id: 'bms-contractors',
            label: 'Contractors',
            icon: 'hardHat',
            route: ROUTE.BMS_CONTRACTORS.path,
            description:
              'Manage external contractors, their billing details, and Xero contact sync.',
            permission: ROUTE.BMS_CONTRACTORS.permission,
          },
          {
            id: 'bms-imports',
            label: 'Billing Imports',
            icon: 'import',
            route: ROUTE.BMS_IMPORTS.path,
            description:
              'Upload a contractor billing spreadsheet, validate it, and track import batches.',
            permission: ROUTE.BMS_IMPORTS.permission,
          },
          {
            id: 'bms-reports',
            label: 'Reports',
            icon: 'chartPie',
            route: ROUTE.BMS_REPORTS.path,
            description:
              'Per-contractor billing totals for a location — service fee owed and the amount to remit.',
            permission: ROUTE.BMS_REPORTS.permission,
          },
          {
            // Restore is its own screen in DocDash, so it gets its own nav entry (not a button on Reports).
            id: 'bms-reports-restore',
            label: 'Restore Periods',
            icon: 'history',
            route: ROUTE.BMS_REPORTS_RESTORE.path,
            description: 'Restore a soft-deleted billing period back to the Reports grid.',
            permission: ROUTE.BMS_REPORTS_RESTORE.permission,
          },
          {
            id: 'bms-billing-settings',
            label: 'Billing Settings',
            icon: 'slidersHorizontal',
            route: ROUTE.BMS_BILLING_SETTINGS.path,
            description:
              'The GST rate applied to future billing imports, and the revenue account and Service Fee account invoices are raised against.',
            permission: ROUTE.BMS_BILLING_SETTINGS.permission,
          },
        ],
      },
    ],
  },
  {
    id: 'admin',
    code: 'SA',
    name: 'Platform Admin',
    icon: 'shield',
    tint: 'mauve',

    groups: [
      {
        id: 'admin-platform',
        label: '',
        icon: 'settings',
        items: [
          {
            id: 'admin-tenants',
            label: 'Companies',
            icon: 'briefcaseBusiness',
            route: ROUTE.ADMIN_TENANTS.path,
            description: 'Manage companies and their subscriptions.',
          },
          {
            id: 'admin-modules',
            label: 'Modules',
            icon: 'boxes',
            route: ROUTE.ADMIN_MODULES.path,
            description: 'Enable or disable platform modules per company.',
          },
          {
            id: 'admin-default-roles',
            label: 'Default Roles',
            icon: 'shieldCheck',
            route: ROUTE.ADMIN_DEFAULT_ROLES.path,
            description: 'Configure baseline roles and permissions for new companies.',
          },
          {
            id: 'admin-users',
            label: 'Platform Users',
            icon: 'userRoundCog',
            route: ROUTE.ADMIN_USERS.path,
            description: 'Every user across every company, with their membership status.',
          },
          {
            id: 'admin-migrations',
            label: 'Migrations',
            icon: 'database',
            route: ROUTE.ADMIN_MIGRATIONS.path,
            description: 'Developer can able to migrate',
          },
          {
            id: 'admin-currencies',
            label: 'Currencies',
            icon: 'coins',
            route: ROUTE.ADMIN_CURRENCIES.path,
            description: 'The currencies every company picks from.',
          },
        ],
      },
    ],
  },
  {
    id: 'dev',
    code: 'DEV',
    name: 'Developer',
    icon: 'code',
    tint: 'orange',

    groups: [
      {
        id: 'dev-ui',
        label: '',
        icon: 'grid',
        items: [
          {
            id: 'dev-controls',
            label: 'Form Controls',
            icon: 'textCursorInput',
            route: ROUTE.DEVELOPER_CONTROLS.path,
            description: 'Browse the shared form control catalog.',
          },
          {
            id: 'dev-grid',
            label: 'Data Grid',
            icon: 'table',
            route: ROUTE.DEVELOPER_GRID.path,
            description: 'Explore the common data grid features.',
          },
          {
            id: 'dev-rich-text-editor',
            label: 'Rich Text Editor',
            icon: 'type',
            route: ROUTE.DEVELOPER_RICH_TEXT_EDITOR.path,
          },
          {
            id: 'dev-procedure-flow',
            label: 'Procedure Flow',
            icon: 'gitBranch',
            route: ROUTE.DEVELOPER_DECISION_FLOW.path,
          },
          {
            id: 'dev-onboarding',
            label: 'Onboarding Demo',
            icon: 'rocket',
            route: ROUTE.DEVELOPER_ONBOARDING.path,
            description: 'Reference list and form implementation.',
          },
          {
            id: 'dev-pdf-gallery',
            label: 'PDF Viewer',
            icon: 'file',
            route: ROUTE.DEVELOPER_PDF_GALLERY.path,
            description: 'Reusable PDF viewer — zoom, search, print, rotate.',
          },
          {
            id: 'dev-media-viewer',
            label: 'Media Viewer',
            icon: 'image',
            route: ROUTE.DEVELOPER_MEDIA_VIEWER.path,
            description: 'Image and video gallery with lightbox.',
          },
          {
            id: 'dev-file-library',
            label: 'File Library',
            icon: 'files',
            route: ROUTE.DEVELOPER_FILE_LIBRARY.path,
            description: 'Mixed PDF + media library using the shared viewers.',
          },
          {
            id: 'folder-explorer',
            label: 'Folder Explorer',
            icon: 'folderTree',
            route: ROUTE.DEVELOPER_FOLDER_EXPLORER.path,
            description: 'Explore folders and documents in a tree view.',
          },
        ],
      },
    ],
  },
  {
    id: 'support',
    code: 'HELP',
    name: 'Help Centre',
    icon: 'lifeBuoy',
    tint: 'pink',
    groups: [
      {
        // Label-less group → the items render directly under HELP (no sub-heading).
        id: 'support-content',
        label: '',
        icon: 'help',
        items: [
          {
            id: 'support-documents',
            label: 'Articles',
            icon: 'bookOpen',
            route: ROUTE.SUPPORT_DOCUMENTS.path,
            description: 'Author, publish and organise help articles.',
            permission: ROUTE.SUPPORT_DOCUMENTS.permission,
          },
          {
            id: 'support-categories',
            label: 'Categories',
            icon: 'folder',
            route: ROUTE.SUPPORT_CATEGORIES.path,
            description: 'The topics articles are filed under.',
            permission: ROUTE.SUPPORT_CATEGORIES.permission,
          },
        ],
      },
    ],
  },
];

export const NAVIGATION: NavModule[] = MODULES.map((module) =>
  SUPER_ADMIN_MODULE_IDS.has(module.id) ? { ...module, superAdminOnly: true } : module
);

export const COMPANY_NAVIGATION: NavModule = {
  id: 'company',
  code: 'CO',
  name: 'Company',
  icon: 'building2',
  tint: 'violet',
  groups: [
    {
      id: 'company-org',
      label: 'Organisation',
      icon: 'building',
      items: [
        {
          id: 'company-profile',
          label: 'Company Profile',
          icon: 'building',
          route: ROUTE.COMPANY_PROFILE.path,
          permission: ROUTE.COMPANY_PROFILE.permission,
        },
        {
          id: 'company-settings',
          label: 'Company Settings',
          icon: 'settings2',
          route: ROUTE.COMPANY_SETTINGS.path,
          permission: ROUTE.COMPANY_SETTINGS.permission,
          description:
            'Company preferences, workforce reference data, leave, templates and kiosk devices.',
        },
      ],
    },
    {
      id: 'company-workforce',
      label: 'Workforce Structure',
      icon: 'users',
      items: [
        {
          id: 'company-locations',
          label: 'Locations',
          icon: 'mapPin',
          route: ROUTE.COMPANY_LOCATIONS.path,
          permission: ROUTE.COMPANY_LOCATIONS.permission,
        },
        {
          id: 'company-teams',
          label: 'Teams',
          icon: 'usersRound',
          route: ROUTE.COMPANY_TEAMS.path,
          permission: ROUTE.COMPANY_TEAMS.permission,
        },
        {
          id: 'company-positions',
          label: 'Positions',
          icon: 'briefcase',
          route: ROUTE.COMPANY_POSITIONS.path,
          permission: ROUTE.COMPANY_POSITIONS.permission,
        },
        {
          id: 'company-organization',
          label: 'Organisation Chart',
          icon: 'network',
          route: ROUTE.COMPANY_ORGANIZATION.path,
          permission: ROUTE.COMPANY_ORGANIZATION.permission,
        },
      ],
    },
    {
      id: 'company-access',
      label: 'Access',
      icon: 'shield',
      items: [
        {
          id: 'company-users',
          label: 'Company Users',
          icon: 'userCog',
          route: ROUTE.COMPANY_USERS.path,
          permission: ROUTE.COMPANY_USERS.permission,
        },
        {
          id: 'company-roles',
          label: 'Roles & Permissions',
          icon: 'keyRound',
          route: ROUTE.COMPANY_ROLES.path,
          permission: ROUTE.COMPANY_ROLES.permission,
        },
      ],
    },
    {
      // Bringing an existing company's data into H4C is company-level setup, not day-to-day PMS work — it
      // creates teams and tags as well as documents, and it's done once when onboarding. The screen itself
      // stays in the PMS module (that's where the documents it creates live); only its entry point sits here.
      id: 'company-import',
      label: 'Import & Integrations',
      icon: 'plug',
      items: [
        {
          id: 'settings-xero',
          label: 'Xero Integration',
          icon: 'plug',
          route: ROUTE.COMPANY_XERO.path,
          description:
            'Connect this company to a Xero organisation to sync employees, leave types and timesheets.',
          permission: ROUTE.COMPANY_XERO.permission,
        },
        {
          id: 'company-import-sweetprocess',
          label: 'SweetProcess Import',
          icon: 'upload',
          description: `Bring your teams, tags, policies, procedures and processes across from SweetProcess.
            Documents are created as <strong>Draft</strong> so you can review them before they go live.`,
          route: ROUTE.PMS_IMPORT_SWEETPROCESS.path,
          permission: ROUTE.PMS_IMPORT_SWEETPROCESS.permission,
        },
      ],
    },
  ],
};

/** Installing H4C itself, as a desktop or kiosk app. The route stays under /settings. */
export const PLATFORM_APP_NAVIGATION: NavModule = {
  id: 'platform-app',
  code: 'APP',
  name: 'Platform Application',
  icon: 'monitorSmartphone',
  tint: 'indigo',
  groups: [
    {
      id: 'settings-kiosk-group',
      label: 'Install Application',
      icon: 'tabletSmartphone',
      items: [
        {
          id: 'settings-install-app',
          label: 'Platform & Kiosk App',
          icon: 'tabletSmartphone',
          route: ROUTE.SETTINGS_INSTALL_APP.path,
          description: 'Install Health4Community as a desktop or tablet app.',
          adminOnly: true,
        },
      ],
    },
  ],
};

/**
 * The sections of Company Settings (`/company/settings/*`) — the rail on that screen, not the sidebar,
 * where the one "Company Settings" leaf stands for all of them. It is still part of ALL_NAVIGATION so
 * each section gets its own breadcrumb, header, tab title, search hit and link-picker entry.
 */
export const COMPANY_SETTINGS_NAVIGATION: NavModule = {
  id: 'company-settings',
  code: 'CO',
  name: 'Company Settings',
  icon: 'settings2',
  tint: 'violet',
  groups: [
    {
      id: 'company-settings-org',
      label: 'Organisation',
      icon: 'building',
      items: [
        {
          id: 'company-settings-general',
          label: 'General',
          icon: 'building',
          route: ROUTE.COMPANY_SETTINGS_GENERAL.path,
          description: 'The currency the company reports in.',
          permission: ROUTE.COMPANY_SETTINGS_GENERAL.permission,
        },
        {
          id: 'company-settings-payroll',
          label: 'Payroll',
          icon: 'dollar',
          route: ROUTE.COMPANY_SETTINGS_PAYROLL.path,
          description: 'The overtime and public holiday rates approved hours are paid at.',
          permission: ROUTE.COMPANY_SETTINGS_PAYROLL.permission,
        },
        {
          id: 'company-settings-security',
          label: 'Security',
          icon: 'shield',
          route: ROUTE.COMPANY_SETTINGS_SECURITY.path,
          description: 'How long a session stays signed in.',
          permission: ROUTE.COMPANY_SETTINGS_SECURITY.permission,
        },
        {
          id: 'settings-branding',
          label: 'Branding',
          icon: 'palette',
          route: ROUTE.SETTINGS_BRANDING.path,
          description: 'Organisation · Logo, email header & footer',
          permission: ROUTE.SETTINGS_BRANDING.permission,
        },
      ],
    },
    {
      id: 'settings-workforce',
      label: 'Workforce Reference Data',
      icon: 'users',
      items: [
        {
          id: 'settings-division',
          label: 'Divisions',
          icon: 'layers',
          route: ROUTE.SETTINGS_DIVISION.path,
          permission: ROUTE.SETTINGS_DIVISION.permission,
        },
        {
          id: 'settings-job-title',
          label: 'Job Titles',
          icon: 'idCard',
          route: ROUTE.SETTINGS_JOB_TITLE.path,
          permission: ROUTE.SETTINGS_JOB_TITLE.permission,
        },
      ],
    },
    {
      id: 'settings-employment',
      label: 'Employment & Payroll',
      icon: 'dollar',
      items: [
        {
          id: 'settings-employment-status',
          label: 'Employment Statuses',
          icon: 'badgeCheck',
          route: ROUTE.SETTINGS_EMPLOYMENT_STATUS.path,
          permission: ROUTE.SETTINGS_EMPLOYMENT_STATUS.permission,
        },
        {
          id: 'settings-pay-group',
          label: 'Pay Groups',
          icon: 'wallet',
          route: ROUTE.SETTINGS_PAY_GROUP.path,
          permission: ROUTE.SETTINGS_PAY_GROUP.permission,
        },
        {
          id: 'settings-industrial-award',
          label: 'Industrial Awards',
          icon: 'award',
          route: ROUTE.SETTINGS_INDUSTRIAL_AWARD.path,
          permission: ROUTE.SETTINGS_INDUSTRIAL_AWARD.permission,
        },
        {
          id: 'settings-award-classification',
          label: 'Award Classifications',
          icon: 'listTree',
          route: ROUTE.SETTINGS_AWARD_CLASSIFICATION.path,
          permission: ROUTE.SETTINGS_AWARD_CLASSIFICATION.permission,
        },
        {
          id: 'settings-tax-residency-status',
          label: 'Tax Residency Statuses',
          icon: 'landmark',
          route: ROUTE.SETTINGS_TAX_RESIDENCY_STATUS.path,
          permission: ROUTE.SETTINGS_TAX_RESIDENCY_STATUS.permission,
        },
      ],
    },
    {
      id: 'settings-leave',
      label: 'Leave & Holidays',
      icon: 'calendar',
      items: [
        {
          id: 'settings-leave-type',
          label: 'Leave Types',
          icon: 'calendarOff',
          route: ROUTE.SETTINGS_LEAVE_TYPE.path,
          permission: ROUTE.SETTINGS_LEAVE_TYPE.permission,
        },
        {
          id: 'settings-public-holidays',
          label: 'Public Holidays',
          icon: 'calendarHeart',
          route: ROUTE.SETTINGS_PUBLIC_HOLIDAYS.path,
          permission: ROUTE.SETTINGS_PUBLIC_HOLIDAYS.permission,
        },
      ],
    },
    {
      id: 'settings-communication',
      label: 'Templates',
      icon: 'inbox',
      items: [
        {
          id: 'settings-email-templates',
          label: 'Email Templates',
          icon: 'mail',
          route: ROUTE.SETTINGS_EMAIL_TEMPLATES.path,
          permission: ROUTE.SETTINGS_EMAIL_TEMPLATES.permission,
        },
      ],
    },
    {
      id: 'settings-attendance-kiosk',
      label: 'Attendance Kiosk',
      icon: 'tabletSmartphone',
      items: [
        {
          id: 'settings-kiosk',
          label: 'Kiosk Devices',
          icon: 'monitorSmartphone',
          route: ROUTE.SETTINGS_KIOSK_DEVICES.path,
          description: 'Manage attendance kiosk devices.',
          permission: ROUTE.SETTINGS_KIOSK_DEVICES.permission,
        },
      ],
    },
  ],
};

export const ALL_NAVIGATION: NavModule[] = [
  ...NAVIGATION,
  COMPANY_NAVIGATION,
  COMPANY_SETTINGS_NAVIGATION,
  PLATFORM_APP_NAVIGATION,
];

const navModules = (...ids: string[]): NavModule[] =>
  ids.flatMap((id) => ALL_NAVIGATION.filter((module) => module.id === id));

/**
 * The sidebar's order, below the pinned Dashboard / Inbox / Shift Schedule links: day-to-day work
 * first, then everything that configures the company or the platform, then Help on its own. A later
 * section with a null label is set off by a plain divider instead of a heading.
 */
export const SIDEBAR_SECTIONS: NavSection[] = [
  { id: 'work', label: null, modules: navModules('my', 'hrm', 'pms', 'bms') },
  {
    id: 'administration',
    label: 'Administration',
    modules: navModules('company', 'platform-app', 'admin', 'dev'),
  },
  { id: 'help', label: null, modules: navModules('support') },
];

/** Pass `modules` to match within a subset — the sidebar only knows its own entries, so a Company
 *  Settings section has to resolve to the one "Company Settings" leaf there, not to the section itself. */
export function findNavMatchByRoute(
  url: string,
  modules: NavModule[] = ALL_NAVIGATION
): NavMatch | null {
  let best: NavMatch | null = null;
  for (const module of modules) {
    for (const group of module.groups) {
      for (const leaf of group.items) {
        const isMatch = url === leaf.route || url.startsWith(leaf.route + '/');
        if (isMatch && (!best || leaf.route.length > best.leaf.route.length)) {
          best = { module, group, leaf };
        }
      }
    }
  }
  return best;
}

export function filterNavigation(
  modules: NavModule[],
  opts: {
    isSuperAdmin: boolean;
    hasPermission: (key: string) => boolean;
    /** The user's type is ExternalContractor — reveals the contractor-only self-service leaves. */
    isExternalContractor?: boolean;
    /** The user holds the ClinicAdmin role — reveals `adminOnly` leaves alongside Super Admins. */
    isClinicAdmin?: boolean;
  }
): NavModule[] {
  const { isSuperAdmin, hasPermission, isExternalContractor = false, isClinicAdmin = false } = opts;
  // A leaf without a permission key is deliberately self-service (My Space items) and shows to
  // every authenticated user — every entitlement-gated screen MUST carry a permission key. A
  // contractor-only leaf is gated on the user's TYPE instead: no permission grant can reveal it,
  // because it is a personal screen, not a capability.
  const leafVisible = (leaf: NavLeaf): boolean => {
    if (leaf.externalContractorOnly && !isExternalContractor) return false;
    if (leaf.adminOnly && !isSuperAdmin && !isClinicAdmin) return false;
    if (!leaf.permission) return true;
    return Array.isArray(leaf.permission)
      ? leaf.permission.some(hasPermission)
      : hasPermission(leaf.permission);
  };

  return modules
    .filter((module) => isSuperAdmin || !module.superAdminOnly)
    .map((module) => ({
      ...module,
      groups: module.groups
        .map((group) => ({ ...group, items: group.items.filter(leafVisible) }))
        .filter((group) => group.items.length > 0),
    }))
    .filter((module) => module.groups.length > 0);
}

export interface Crumb {
  label: string;
  link?: string;
  accent?: boolean;
  current?: boolean;
}

export function buildCrumbsFromRoute(url: string): Crumb[] {
  if (url.startsWith('/dashboard')) {
    return [{ label: 'Dashboard', current: true }];
  }

  const crumbs: Crumb[] = [{ label: 'Dashboard', link: '/dashboard' }];
  const match = findNavMatchByRoute(url);
  // A Company Settings section lives inside the shell, so its trail is the shell's own (Company ›
  // Organisation › Company Settings) with the section after it — not the rail's group headings.
  if (match?.module.id === COMPANY_SETTINGS_NAVIGATION.id) {
    const shell = buildCrumbsFromRoute(ROUTE.COMPANY_SETTINGS.path).map((crumb) =>
      crumb.current ? { label: crumb.label, link: ROUTE.COMPANY_SETTINGS.path } : crumb
    );
    return [...shell, { label: match.leaf.label, current: true }];
  }
  if (match) {
    crumbs.push({
      label: match.module.name || match.module.code,
      accent: true,
      link: match.module.groups[0].items[0].route,
    });
    // Label-less groups (e.g. the direct-link ME items) would otherwise emit a blank crumb segment.
    if (match.group.label) {
      crumbs.push({ label: match.group.label, link: match.group.items[0].route });
    }
    // The breadcrumb always stops at the list/section leaf — a record's own name is the page's bold
    // title (page-header), never repeated as a trailing crumb. On a detail/child route (…/procedures/:id)
    // the leaf stays a link back to its list; on the list route itself it is the current (non-link) crumb.
    const onLeaf = url === match.leaf.route;
    crumbs.push(
      onLeaf
        ? { label: match.leaf.label, current: true }
        : { label: match.leaf.label, link: match.leaf.route }
    );
  }
  return crumbs;
}

export function buildPageHeaderFromRoute(url: string): PageHeader | null {
  const match = findNavMatchByRoute(url);
  if (!match) {
    return null;
  }
  return {
    title: match.leaf.label,
    eyebrow: match.group.label
      ? `${match.module.name || match.module.code} · ${match.group.label}`
      : match.module.name || match.module.code,
    icon: match.leaf.icon,
    iconTint: match.module.tint,
    description: match.leaf.description,
  };
}

export function findNavMatchById(id: string): NavMatch | null {
  for (const module of ALL_NAVIGATION) {
    for (const group of module.groups) {
      const leaf = group.items.find((item) => item.id === id);
      if (leaf) {
        return { module, group, leaf };
      }
    }
  }
  return null;
}
